// Turns an Apollo error into something a form can show:
//   fieldErrorsOf(error) → { global, fields: { [name]: message }, code }
//
// The API throws ApolloError({ code, message, field?, fields? }) and its
// formatError hands that object to the client as graphQLErrors[i]. `fields`
// (when present) lists every invalid input as [{ field, message }], and
// `field` repeats the first one. Both land in `fields`, first message per
// key wins, so the specific messages beat the "N fields need your
// attention" summary. `global` is the top-level message and is always set:
// the page toasts it. Raw SQL (an unhandled database error passed through)
// never reaches the screen. A network failure gets its own message.
//
// Same contract as users-v3's UsersV3/errors.js (to be consolidated, D19).

export const GENERIC_ERROR = 'Something went wrong. Please try again.';
export const NETWORK_ERROR =
  "Couldn't reach the server. Check your connection and try again.";

// "GraphQL error: Error: Invalid email" → "Invalid email" (the resolver
// error wrapper can stack more than one "Error: ").
export const cleanMessage = (message) => {
  let text = String(message == null ? '' : message).replace(
    /^\s*(GraphQL error|Network error)\s*:\s*/i,
    ''
  );
  while (/^\s*Error\s*:\s*/.test(text)) {
    text = text.replace(/^\s*Error\s*:\s*/, '');
  }
  return text.trim();
};

const SQL_PATTERNS = [
  /duplicate key value/i,
  /violates (unique|foreign key|not-null|check) constraint/i,
  /\binsert into\s+"/i,
  /\bupdate\s+"[a-z_]+"\s+set\b/i,
  /\bselect\s+("|\*)/i,
  /\bdelete from\s+"/i,
  /\brelation\s+"[^"]+"\s+does not exist/i,
  /\bcolumn\s+"[^"]+"/i,
  /syntax error at or near/i,
  /null value in column/i,
  /Empty \.update\(\) call/i,
  /Undefined binding\(s\)/i,
];

export const looksLikeSql = (message) =>
  SQL_PATTERNS.some((rx) => rx.test(message || ''));

// A message that arrived as the ApolloError JSON (a path that skipped the
// API's formatError): {"code":400,"field":"name","message":"…"}.
const unwrapJson = (message) => {
  if (!/^\{/.test(message)) return null;
  try {
    const parsed = JSON.parse(message);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
};

const readable = (message) => {
  const text = cleanMessage(message);
  if (!text || looksLikeSql(text)) return GENERIC_ERROR;
  return text;
};

// { code, message, field, fields } with any JSON-string message unpacked.
const normalizeEntry = (entry) => {
  const e = entry || {};
  let out = { code: e.code, message: cleanMessage(e.message), field: e.field };
  out.fields = Array.isArray(e.fields) ? e.fields : [];
  const json = unwrapJson(out.message);
  if (json) {
    out = {
      code: out.code != null ? out.code : json.code,
      message: cleanMessage(json.message),
      field: out.field || json.field,
      fields: out.fields.length
        ? out.fields
        : (Array.isArray(json.fields) && json.fields) || [],
    };
  }
  if (out.code == null && e.extensions && e.extensions.code != null) {
    out.code = e.extensions.code;
  }
  return out;
};

const entriesOf = (error) => {
  if (error.graphQLErrors && error.graphQLErrors.length) {
    return error.graphQLErrors;
  }
  if (error.networkError) return null;
  return [{ message: error.message, code: error.code, field: error.field }];
};

export const fieldErrorsOf = (error) => {
  const out = { global: GENERIC_ERROR, fields: {}, code: null };
  if (!error) return out;
  const entries = entriesOf(error);
  if (!entries) {
    out.global = NETWORK_ERROR;
    return out;
  }

  let global = null;
  entries.map(normalizeEntry).forEach((entry) => {
    if (out.code == null && entry.code != null) out.code = entry.code;
    const sql = looksLikeSql(entry.message);
    const message = readable(entry.message);
    if (!global) global = message;
    if (sql) return;
    entry.fields.forEach((f) => {
      if (f && f.field && !out.fields[f.field]) {
        out.fields[f.field] = readable(f.message);
      }
    });
    if (entry.field && !out.fields[entry.field]) {
      out.fields[entry.field] = message;
    }
  });

  out.global = global || GENERIC_ERROR;
  return out;
};

export default fieldErrorsOf;
